import type { DataIssue, DataQuality, IndicatorSeries, Observation } from "../types";
import { deriveDifference, unavailableSeries } from "./derived.ts";
import { isUsableSeries } from "./freshness.ts";

export const LEGACY_MORTGAGE_SERIES_IDS = {
  existingMortgageCost: "mortgage_existing_cost",
  repricingExposure: "mortgage_stock_repricing_exposure",
  mortgageBalance: "mortgage_stock_balance",
  housingPriceOutlook: "housing_price_outlook_csi",
  expectedInflation: "expected_inflation_households"
} as const;

export const LEGACY_MORTGAGE_EXPECTED_INFLATION_CANDIDATE_IDS = {
  oneYear: "expected_inflation_households_1y",
  threeYear: "expected_inflation_households_3y",
  fiveYear: "expected_inflation_households_5y"
} as const;

export type LegacyMortgageGateState = "ready" | "pending_metadata" | "pending_decision" | "unavailable";
export type LegacyMortgageAvailability = "DATA" | "STALE" | "N/A";
export type LegacyMortgageExpectedInflationHorizon = "1Y" | "3Y" | "5Y";
export type LegacyMortgageExpectedInflationView = "COMPARE" | LegacyMortgageExpectedInflationHorizon;

export interface LegacyMortgageInputGate {
  id: keyof typeof LEGACY_MORTGAGE_SERIES_IDS | "newMortgageRate";
  label: string;
  state: LegacyMortgageGateState;
  reason: string;
  seriesId: string;
}

export interface LegacyMortgageSnapshot {
  latestMonth: string | null;
  current: number | null;
  change1m: number | null;
  change6m: number | null;
  availability: LegacyMortgageAvailability;
}

export interface LegacyMortgageStressBundle {
  inputs: {
    newMortgageRate: IndicatorSeries;
    existingMortgageCost: IndicatorSeries;
    repricingExposure: IndicatorSeries;
    mortgageBalance: IndicatorSeries;
    housingPriceOutlook: IndicatorSeries;
    expectedInflation: IndicatorSeries;
  };
  expectedInflationCandidates: {
    oneYear?: IndicatorSeries;
    threeYear?: IndicatorSeries;
    fiveYear?: IndicatorSeries;
  };
  lockInSpread: IndicatorSeries;
  repricingPressure: IndicatorSeries;
  aggregateAnnualInterestProxy: IndicatorSeries;
  cashFlowStressProxy: IndicatorSeries;
  snapshots: {
    newMortgageRate: LegacyMortgageSnapshot;
    existingMortgageCost: LegacyMortgageSnapshot;
    lockInSpread: LegacyMortgageSnapshot;
    repricingPressure: LegacyMortgageSnapshot;
    aggregateAnnualInterestProxy: LegacyMortgageSnapshot;
  };
  gates: LegacyMortgageInputGate[];
  status: {
    state: "ready" | "partial" | "pending_metadata";
    repricingLabel: "DATA" | "N/A";
    cashFlowStressLabel: "N/A";
    classification: "N/A";
    classificationReason: string;
    expectedInflationHorizon: "USER_SELECTABLE" | "UNAVAILABLE";
  };
  issues: DataIssue[];
  interpretation: {
    observed: string;
    mechanism: string;
    inference: string;
  };
}

export interface BuildLegacyMortgageStressArgs {
  newMortgageRate: IndicatorSeries;
  existingMortgageCost?: IndicatorSeries;
  repricingExposure?: IndicatorSeries;
  mortgageBalance?: IndicatorSeries;
  housingPriceOutlook?: IndicatorSeries;
  expectedInflation?: IndicatorSeries;
  expectedInflationCandidates?: {
    oneYear?: IndicatorSeries;
    threeYear?: IndicatorSeries;
    fiveYear?: IndicatorSeries;
  };
}

const METADATA_PENDING = "현행 ECOS STAT_CODE/ITEM_CODE, 기준(flow/stock), 금융기관 범위를 exact metadata로 확인한 뒤에만 연결합니다.";

function placeholder(
  id: string,
  name: string,
  shortName: string,
  unit: IndicatorSeries["unit"],
  note: string
) {
  return unavailableSeries(id, name, shortName, "real_estate", "monthly", unit, note);
}

export function legacyMortgagePlaceholders() {
  return {
    existingMortgageCost: placeholder(
      LEGACY_MORTGAGE_SERIES_IDS.existingMortgageCost,
      "기존차주 주담대 잔액금리",
      "Existing mortgage",
      "%",
      "H1B Gate A exact selector는 2026-08-30 live ECOS metadata에서 121Y015/BECBLB020202(예금은행 주택담보대출 잔액금리)로 검증되었습니다. 이 placeholder가 보이면 runtime ECOS metadata/data load 실패를 확인하십시오. 잔액 총대출금리나 신규취급 주담대금리로 대체하지 않습니다."
    ),
    repricingExposure: placeholder(
      LEGACY_MORTGAGE_SERIES_IDS.repricingExposure,
      "기존 주담대 stock 재가격 노출비중",
      "Repricing exposure",
      "%",
      "H1B Gate B exact selector는 2026-08-30 live ECOS metadata에서 121Y011/LR20000(주택담보대출-변동금리대출 비중, 잔액 기준)으로 검증되었습니다. 이 placeholder가 보이면 runtime ECOS metadata/data load 실패를 확인하십시오. 신규취급 고정/변동 비중을 기존 stock exposure로 대체하지 않습니다."
    ),
    mortgageBalance: placeholder(
      LEGACY_MORTGAGE_SERIES_IDS.mortgageBalance,
      "주담대 잔액",
      "Mortgage balance",
      "trillion_krw",
      `H1B Gate C pending. ${METADATA_PENDING} Existing Mortgage Cost와 모집단이 일치하지 않으면 balance × rate 계산을 하지 않습니다.`
    ),
    housingPriceOutlook: placeholder(
      LEGACY_MORTGAGE_SERIES_IDS.housingPriceOutlook,
      "주택가격전망 CSI",
      "Housing outlook",
      "index",
      "H1B Gate D exact selector는 2026-08-30 실배포 ECOS metadata에서 511Y002 Group1=FMFB(주택가격전망CSI) + Group2=99988(전체), 월별로 검증되었습니다. 이 placeholder가 보이면 runtime ECOS metadata/data load issue를 확인하십시오."
    ),
    expectedInflation: placeholder(
      LEGACY_MORTGAGE_SERIES_IDS.expectedInflation,
      "기대인플레이션",
      "Expected inflation",
      "%",
      "H1B Gate E raw metadata는 511Y003/FMB(향후1년), FMC(3년후), FMD(5년후)로 모두 exact 확인되었습니다. 모델에 사용할 horizon을 임의 선택하지 않으므로 선택 전에는 generic series를 N/A로 유지합니다."
    )
  };
}

function qualityFor(...series: IndicatorSeries[]): DataQuality {
  if (series.some((s) => s.source.quality === "unavailable")) return "unavailable";
  if (series.some((s) => s.source.quality === "stale")) return "stale";
  if (series.some((s) => s.source.quality === "demo")) return "demo";
  return "official";
}

function monthKey(date: string) {
  const month = date.slice(0, 7);
  return /^\d{4}-\d{2}$/.test(month) ? month : null;
}

function monthShift(month: string, delta: number) {
  const match = month.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthlyLevelMap(series: IndicatorSeries) {
  const values = new Map<string, number>();
  for (const obs of series.observations) {
    if (obs.value == null || !Number.isFinite(obs.value)) continue;
    const month = monthKey(obs.date);
    if (!month) continue;
    // H1B inputs are expected to be monthly official series. If duplicate
    // observations exist for one month, fail closed instead of averaging an
    // unknown frequency/definition into a fabricated monthly level.
    if (values.has(month)) return null;
    values.set(month, obs.value);
  }
  return values;
}

function monthlyObservations(map: Map<string, number | null>): Observation[] {
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, value]) => ({ date: `${month}-01`, value }));
}

function snapshot(series: IndicatorSeries): LegacyMortgageSnapshot {
  const levels = monthlyLevelMap(series);
  if (!levels || levels.size === 0) {
    return {
      latestMonth: null,
      current: null,
      change1m: null,
      change6m: null,
      availability: series.source.quality === "stale" ? "STALE" : "N/A"
    };
  }
  const latestMonth = [...levels.keys()].sort().at(-1) ?? null;
  if (!latestMonth) return { latestMonth: null, current: null, change1m: null, change6m: null, availability: "N/A" };
  const current = levels.get(latestMonth) ?? null;
  const prev1 = monthShift(latestMonth, -1);
  const prev6 = monthShift(latestMonth, -6);
  const v1 = prev1 ? levels.get(prev1) : undefined;
  const v6 = prev6 ? levels.get(prev6) : undefined;
  return {
    latestMonth,
    current,
    change1m: current != null && v1 != null ? Number((current - v1).toFixed(6)) : null,
    change6m: current != null && v6 != null ? Number((current - v6).toFixed(6)) : null,
    availability: series.source.quality === "stale" ? "STALE" : current == null ? "N/A" : "DATA"
  };
}

function derivedMonthlySeries(args: {
  id: string;
  name: string;
  shortName: string;
  unit: IndicatorSeries["unit"];
  inputs: IndicatorSeries[];
  observations: Observation[];
  dataset: string;
  note: string;
}): IndicatorSeries {
  const observationDate = [...args.observations].reverse().find((o) => o.value != null)?.date;
  return {
    id: args.id,
    name: args.name,
    shortName: args.shortName,
    unit: args.unit,
    layer: "real_estate",
    source: {
      provider: "Calculated",
      dataset: args.dataset,
      url: "",
      frequency: "monthly",
      quality: qualityFor(...args.inputs),
      observationDate,
      collectedAt: args.inputs.map((s) => s.source.collectedAt).filter((x): x is string => Boolean(x)).sort().at(-1),
      note: args.note,
      sourceIds: args.inputs.map((s) => s.id)
    },
    observations: args.observations
  };
}


function buildLockInSpread(newMortgageRate: IndicatorSeries, existingMortgageCost: IndicatorSeries): IndicatorSeries {
  if (newMortgageRate.unit !== "%" || existingMortgageCost.unit !== "%") {
    return placeholder(
      "mortgage_lock_in_spread",
      "신규 주담대금리 - 기존차주 주담대 잔액금리",
      "Lock-in spread",
      "pp",
      "Lock-in Spread 입력은 신규·기존 주담대 금리(%)여야 하므로 현재 단위가 다르면 N/A입니다."
    );
  }
  if (newMortgageRate.source.frequency !== "monthly" || existingMortgageCost.source.frequency !== "monthly") {
    return placeholder(
      "mortgage_lock_in_spread",
      "신규 주담대금리 - 기존차주 주담대 잔액금리",
      "Lock-in spread",
      "pp",
      "H1B Lock-in Spread는 공식 월별 주담대 금리끼리 exact-match합니다. 다른 주기의 계열을 임의 월변환하지 않습니다."
    );
  }
  return deriveDifference({
    id: "mortgage_lock_in_spread",
    name: "신규 주담대금리 - 기존차주 주담대 잔액금리",
    shortName: "Lock-in spread",
    a: newMortgageRate,
    b: existingMortgageCost,
    unit: "pp",
    layer: "real_estate",
    alignment: "exact",
    scale: 1,
    decimals: 4,
    note: "Monthly exact-match only. This spread describes the financing-cost gap faced when replacing an existing average mortgage with a newly originated mortgage; it is not converted into a sale or move probability."
  });
}

function buildRepricingPressure(existingRate: IndicatorSeries, exposure: IndicatorSeries): IndicatorSeries {
  if (existingRate.unit !== "%" || exposure.unit !== "%") {
    return placeholder(
      "legacy_repricing_pressure",
      "기존 주담대 재가격 압력",
      "Legacy repricing",
      "pp",
      "입력 단위가 Existing Mortgage Rate(%) × stock exposure(%) 조건과 일치하지 않아 N/A입니다."
    );
  }
  const rate = monthlyLevelMap(existingRate);
  const exp = monthlyLevelMap(exposure);
  if (!rate || !exp) {
    return placeholder(
      "legacy_repricing_pressure",
      "기존 주담대 재가격 압력",
      "Legacy repricing",
      "pp",
      "동일 월에 중복 관측이 있어 월 원계열 정의를 확정할 수 없으므로 N/A입니다."
    );
  }
  const out = new Map<string, number | null>();
  for (const month of [...rate.keys()].sort()) {
    const current = rate.get(month);
    const previousMonth = monthShift(month, -1);
    const previous = previousMonth ? rate.get(previousMonth) : undefined;
    const exposurePct = exp.get(month);
    if (current == null || previous == null || exposurePct == null || exposurePct < 0 || exposurePct > 100) {
      out.set(month, null);
      continue;
    }
    out.set(month, Number(((current - previous) * exposurePct / 100).toFixed(6)));
  }
  return derivedMonthlySeries({
    id: "legacy_repricing_pressure",
    name: "기존 주담대 재가격 압력",
    shortName: "Legacy repricing",
    unit: "pp",
    inputs: [existingRate, exposure],
    observations: monthlyObservations(out),
    dataset: "Δ Existing Mortgage Cost × existing mortgage stock repricing exposure",
    note: "Exact prior calendar month only. Missing months remain N/A. Exposure is interpreted as 0~100% of existing mortgage stock only; new-origination fixed/variable shares are not accepted as a substitute. This is a macro repricing proxy, not a borrower payment change or default probability."
  });
}

function buildAggregateInterestProxy(balance: IndicatorSeries, existingRate: IndicatorSeries): IndicatorSeries {
  if (balance.unit !== "trillion_krw" || existingRate.unit !== "%") {
    return placeholder(
      "legacy_mortgage_annual_interest_proxy",
      "기존 주담대 단순 연이자 노출 proxy",
      "Legacy interest proxy",
      "trillion_krw",
      "Mortgage balance(조원)와 Existing Mortgage Cost(%)의 단위/모집단 조건을 확인할 수 없어 N/A입니다."
    );
  }
  const balanceMap = monthlyLevelMap(balance);
  const rateMap = monthlyLevelMap(existingRate);
  if (!balanceMap || !rateMap) {
    return placeholder(
      "legacy_mortgage_annual_interest_proxy",
      "기존 주담대 단순 연이자 노출 proxy",
      "Legacy interest proxy",
      "trillion_krw",
      "동일 월 중복 관측이 있어 exact monthly alignment를 확정할 수 없으므로 N/A입니다."
    );
  }
  const months = [...new Set([...balanceMap.keys(), ...rateMap.keys()])].sort();
  const out = new Map<string, number | null>();
  for (const month of months) {
    const b = balanceMap.get(month);
    const r = rateMap.get(month);
    out.set(month, b == null || r == null || b < 0 || r < 0 ? null : Number((b * r / 100).toFixed(6)));
  }
  return derivedMonthlySeries({
    id: "legacy_mortgage_annual_interest_proxy",
    name: "기존 주담대 단순 연이자 노출 proxy",
    shortName: "Legacy interest proxy",
    unit: "trillion_krw",
    inputs: [balance, existingRate],
    observations: monthlyObservations(out),
    dataset: "Mortgage balance × Existing Mortgage Cost",
    note: "Same-month exact alignment only. This is a simple annualized interest-exposure proxy (balance × annual rate), not scheduled principal-and-interest payments. It is valid only when balance and rate metadata refer to the same mortgage population and institution scope."
  });
}

function gateFor(
  id: LegacyMortgageInputGate["id"],
  label: string,
  series: IndicatorSeries,
  reasonWhenPending: string
): LegacyMortgageInputGate {
  const hasData = isUsableSeries(series) && series.observations.some((o) => o.value != null);
  return {
    id,
    label,
    state: hasData ? "ready" : series.source.quality === "unavailable" ? "pending_metadata" : "unavailable",
    reason: hasData ? "Exact production series is loaded and has numeric observations." : reasonWhenPending,
    seriesId: series.id
  };
}

function candidateForHorizon(
  candidates: LegacyMortgageStressBundle["expectedInflationCandidates"],
  horizon: LegacyMortgageExpectedInflationHorizon
): IndicatorSeries | undefined {
  if (horizon === "1Y") return candidates.oneYear;
  if (horizon === "3Y") return candidates.threeYear;
  return candidates.fiveYear;
}

export function resolveExpectedInflationSeries(
  candidates: LegacyMortgageStressBundle["expectedInflationCandidates"],
  horizon: LegacyMortgageExpectedInflationHorizon
): IndicatorSeries | null {
  const candidate = candidateForHorizon(candidates, horizon);
  if (!candidate || !isUsableSeries(candidate) || !candidate.observations.some((o) => o.value != null)) return null;
  return candidate;
}

function expectedInflationGate(
  selected: IndicatorSeries,
  candidates: BuildLegacyMortgageStressArgs["expectedInflationCandidates"]
): LegacyMortgageInputGate {
  const selectedReady = isUsableSeries(selected) && selected.observations.some((o) => o.value != null);
  if (selectedReady) {
    return {
      id: "expectedInflation",
      label: "기대인플레이션",
      state: "ready",
      reason: "An exact expected-inflation series is loaded as the selected production series.",
      seriesId: selected.id
    };
  }
  const horizonCandidates: Array<[LegacyMortgageExpectedInflationHorizon, IndicatorSeries | undefined]> = [
    ["1Y", candidates?.oneYear],
    ["3Y", candidates?.threeYear],
    ["5Y", candidates?.fiveYear]
  ];
  const readyHorizons = horizonCandidates
    .filter((entry): entry is [LegacyMortgageExpectedInflationHorizon, IndicatorSeries] => Boolean(entry[1] && isUsableSeries(entry[1]) && entry[1].observations.some((o) => o.value != null)))
    .map(([horizon]) => horizon);
  if (readyHorizons.length === 3) {
    return {
      id: "expectedInflation",
      label: "기대인플레이션",
      state: "ready",
      reason: "1Y / 3Y / 5Y exact household expected-inflation series are all loaded. The H1B UI exposes COMPARE / 1Y / 3Y / 5Y at runtime instead of hard-coding one horizon.",
      seriesId: "expected_inflation_households_{1y|3y|5y}"
    };
  }
  if (readyHorizons.length > 0) {
    return {
      id: "expectedInflation",
      label: "기대인플레이션",
      state: "unavailable",
      reason: `Only ${readyHorizons.join(" / ")} exact horizon(s) are currently usable. H1B requires all 1Y / 3Y / 5Y candidates before enabling the runtime selector.`,
      seriesId: selected.id
    };
  }
  return gateFor(
    "expectedInflation",
    "기대인플레이션",
    selected,
    "511Y003의 1Y/3Y/5Y exact 원계열을 모두 로드해야 runtime 선택기를 활성화합니다."
  );
}

export function buildLegacyMortgageStress(args: BuildLegacyMortgageStressArgs): LegacyMortgageStressBundle {
  const defaults = legacyMortgagePlaceholders();
  const existingMortgageCost = args.existingMortgageCost ?? defaults.existingMortgageCost;
  const repricingExposure = args.repricingExposure ?? defaults.repricingExposure;
  const mortgageBalance = args.mortgageBalance ?? defaults.mortgageBalance;
  const housingPriceOutlook = args.housingPriceOutlook ?? defaults.housingPriceOutlook;
  const expectedInflation = args.expectedInflation ?? defaults.expectedInflation;
  const expectedInflationCandidates = args.expectedInflationCandidates ?? {};

  const lockInSpread = buildLockInSpread(args.newMortgageRate, existingMortgageCost);

  const repricingPressure = buildRepricingPressure(existingMortgageCost, repricingExposure);
  const aggregateAnnualInterestProxy = buildAggregateInterestProxy(mortgageBalance, existingMortgageCost);
  const cashFlowStressProxy = placeholder(
    "legacy_cash_flow_stress_proxy",
    "기존차주 현금흐름 스트레스 proxy",
    "Legacy cash-flow stress",
    "score",
    "Existing Mortgage Cost 상승속도와 Repricing Exposure를 하나의 점수로 합치는 정규화·가중치가 아직 역사검증되지 않았으므로 임의 점수를 생성하지 않습니다."
  );

  const gates: LegacyMortgageInputGate[] = [
    gateFor("newMortgageRate", "신규 주담대금리", args.newMortgageRate, "기존 H1 신규취급 주담대 계열이 로드되지 않았습니다."),
    gateFor("existingMortgageCost", "예금은행 주담대 잔액금리", existingMortgageCost, "Gate A exact selector(121Y015/BECBLB020202)는 검증 완료되었습니다. 현재 N/A이면 runtime ECOS metadata/data load issue를 확인합니다."),
    gateFor("repricingExposure", "기존 mortgage stock 변동금리 노출", repricingExposure, "Gate B exact selector(121Y011/LR20000)는 검증 완료되었습니다. 현재 N/A이면 runtime ECOS metadata/data load issue를 확인합니다."),
    gateFor("mortgageBalance", "주담대 잔액", mortgageBalance, "Gate C: Existing Mortgage Cost와 동일 모집단·금융기관 범위의 주담대 잔액 exact metadata가 필요합니다."),
    gateFor("housingPriceOutlook", "주택가격전망 CSI", housingPriceOutlook, "Gate D exact selector(511Y002 Group1=FMFB + Group2=99988 전체)는 검증 완료되었습니다. 현재 N/A이면 runtime ECOS metadata/data load issue를 확인합니다."),
    expectedInflationGate(expectedInflation, expectedInflationCandidates)
  ];

  const newReady = gates.find((g) => g.id === "newMortgageRate")?.state === "ready";
  const coreReady = gates.find((g) => g.id === "existingMortgageCost")?.state === "ready";
  const repricingReady = gates.find((g) => g.id === "repricingExposure")?.state === "ready";
  const balanceReady = gates.find((g) => g.id === "mortgageBalance")?.state === "ready";
  const statusState = newReady && coreReady && repricingReady && balanceReady
    ? "ready"
    : coreReady
      ? "partial"
      : "pending_metadata";

  const issues: DataIssue[] = gates
    .filter((g) => g.state !== "ready")
    .map((g) => ({
      id: `h1b-${g.id}`,
      severity: "info" as const,
      scope: "housing-h1b",
      message: `${g.label}: ${g.reason}`
    }));

  return {
    inputs: {
      newMortgageRate: args.newMortgageRate,
      existingMortgageCost,
      repricingExposure,
      mortgageBalance,
      housingPriceOutlook,
      expectedInflation
    },
    expectedInflationCandidates,
    lockInSpread,
    repricingPressure,
    aggregateAnnualInterestProxy,
    cashFlowStressProxy,
    snapshots: {
      newMortgageRate: snapshot(args.newMortgageRate),
      existingMortgageCost: snapshot(existingMortgageCost),
      lockInSpread: snapshot(lockInSpread),
      repricingPressure: snapshot(repricingPressure),
      aggregateAnnualInterestProxy: snapshot(aggregateAnnualInterestProxy)
    },
    gates,
    status: {
      state: statusState,
      repricingLabel: isUsableSeries(repricingPressure) && repricingPressure.observations.some((o) => o.value != null) ? "DATA" : "N/A",
      cashFlowStressLabel: "N/A",
      classification: "N/A",
      classificationReason: "Flow Squeeze / Mortgage Lock-in / Legacy Cash-flow Stress / Broad Housing Stress의 정량 임계값과 매물 데이터가 역사검증되지 않았으므로 자동 상태분류를 아직 수행하지 않습니다.",
      expectedInflationHorizon: ["1Y", "3Y", "5Y"].every((horizon) => resolveExpectedInflationSeries(expectedInflationCandidates, horizon as LegacyMortgageExpectedInflationHorizon))
        ? "USER_SELECTABLE"
        : "UNAVAILABLE"
    },
    issues,
    interpretation: {
      observed: coreReady
        ? "신규 주담대금리와 예금은행 주택담보대출 잔액 가중평균금리를 별도 계열로 관측합니다."
        : "신규 주담대금리는 관측하지만 검증된 예금은행 주담대 잔액금리가 runtime에서 로드되지 않아 N/A입니다.",
      mechanism: "신규금리와 기존금리의 격차는 갈아타기 시 금융비용 변화와 정합적인 lock-in 경로를 보여줄 수 있고, 기존 stock 재가격 노출은 금리상승이 기존차주에 전달되는 속도와 관련될 수 있습니다.",
      inference: "Lock-in spread를 매도확률로 바꾸지 않고, 금리상승만으로 강제매도·가격하락을 판정하지 않습니다. Repricing과 현금흐름 점수는 exact stock data와 검증된 정의가 없으면 N/A를 유지합니다."
    }
  };
}
